import type React from 'react';
import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { Breadcrumbs } from '../components/shared/Breadcrumbs';
import { ShireQuickAccess } from '../components/shared/ShireQuickAccess';
import { ShirePdfDownload } from '../components/shared/ShirePdfDownload';
import { ShareButton } from '../components/shared/ShareButton';
import { ShireNeedBar } from '../components/shared/ShireNeedBar';
import { ResponsibilitiesExplainer } from '../components/shared/ResponsibilitiesExplainer';
import { DfesWarningsPanel } from '../components/shared/DfesWarningsPanel';
import { SHIRES, type Shire, type ShireId } from '../components/shared/electorate-shires';
import { SHIRE_CONTENT, type ShireContent } from '../data/shire-content';

const VALID_IDS = new Set(SHIRES.map((s) => s.id));

const SHIRE_ORDER: ShireId[] = [
  'augusta-margaret-river',
  'denmark',
  'boyup-brook',
  'bridgetown-greenbushes',
  'nannup',
  'manjimup',
];

export const Route = createFileRoute('/shire/$name')({
  loader: ({ params }) => {
    if (!VALID_IDS.has(params.name as ShireId)) throw notFound();
    const id = params.name as ShireId;
    const shire = SHIRES.find((s) => s.id === id)!;
    return { shire, content: SHIRE_CONTENT[id] };
  },
  head: (ctx) => {
    const loaderData = (ctx as { loaderData?: { shire: Shire; content: ShireContent } } | undefined)?.loaderData;
    if (!loaderData) return { meta: [{ title: 'Shire — Warren-Blackwood' }] };
    const { shire, content } = loaderData;
    const title = `Shire of ${shire.name} — Warren-Blackwood`;
    return {
      meta: [
        { title },
        { name: 'description', content: content.tagline },
        { property: 'og:title', content: title },
        { property: 'og:description', content: content.tagline },
      ],
    };
  },
  component: ShirePage,
  notFoundComponent: ShireNotFound,
  errorComponent: ({ error }) => (
    <main style={{ padding: '2rem' }}>
      <p>Could not load this shire: {error.message}</p>
    </main>
  ),
});

function ShireNotFound() {
  return (
    <main
      id="main-content"
      style={{ maxWidth: 640, margin: '0 auto', padding: 'var(--sp-lg) var(--sp-md)' }}
    >
      <h1 style={{ fontFamily: 'var(--font-heading)', color: 'var(--green)' }}>Shire not found</h1>
      <p style={{ color: 'var(--ink-mid)', marginTop: '0.5rem' }}>
        That shire isn’t one of the six in Warren-Blackwood.
      </p>
      <p style={{ marginTop: '1rem' }}>
        <Link to="/about" style={{ color: 'var(--green)', textDecoration: 'underline' }}>
          See all six shires →
        </Link>
      </p>
    </main>
  );
}

function ShirePage() {
  const { shire, content } = Route.useLoaderData() as { shire: Shire; content: ShireContent };

  // Pick a single "priorities" paragraph if present, otherwise the first one.
  const prioritiesIdx = content.paragraphs.findIndex((p) =>
    /priorities|conversations|challenges|concerns|agenda|infrastructure gaps|calling for/i.test(p),
  );
  const narrative = content.paragraphs[0];
  const priorities = prioritiesIdx > 0 ? content.paragraphs[prioritiesIdx] : null;

  return (
    <main
      id="main-content"
      className="shire-print-root"
      style={{
        maxWidth: 1100,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <Breadcrumbs
        items={[
          { label: 'Map', to: '/', search: { shire: shire.id } },
          { label: `Shire of ${shire.name}` },
        ]}
      />

      {/* 1. DFES warnings — compact, above the fold */}
      <div className="no-print" style={{ marginBottom: 'var(--sp-md)' }}>
        <DfesWarningsPanel shireId={shire.id} />
      </div>

      {/* 2. Shire identity card: name, logo, seat / population / area / key facts */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-md)',
          flexWrap: 'wrap',
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(2rem, 5vw, 2.75rem)',
            color: 'var(--green)',
            lineHeight: 1.1,
            margin: 0,
            flex: '1 1 auto',
          }}
        >
          Shire of {shire.name}
        </h1>
        {shire.logoUrl ? (
          <div
            style={{
              flex: '0 0 auto',
              width: 'clamp(64px, 12vw, 88px)',
              height: 'clamp(64px, 12vw, 88px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={shire.logoUrl}
              alt={`Shire of ${shire.name} logo`}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </div>
        ) : null}
      </div>

      <div
        className="no-print"
        style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}
      >
        <button
          type="button"
          onClick={() => window.print()}
          style={{
            padding: '0.35rem 0.75rem',
            background: 'transparent',
            color: 'var(--green)',
            border: '1px solid var(--green)',
            borderRadius: 999,
            fontSize: 13,
            fontFamily: 'var(--font-body)',
            cursor: 'pointer',
          }}
        >
          Print this page
        </button>
        <ShirePdfDownload shire={shire} variant="compact" />
        <ShareButton
          title={`Shire of ${shire.name} — Warren-Blackwood guide`}
          text={`Shire of ${shire.name}: ${content.tagline}`}
        />
      </div>

      <p
        style={{
          fontStyle: 'italic',
          color: 'var(--ink-mid)',
          marginTop: '0.5rem',
          marginBottom: 'var(--sp-md)',
          fontSize: '1.05rem',
        }}
      >
        {content.tagline}
      </p>

      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '0.75rem',
          background: 'var(--gold-pale)',
          border: '1px solid var(--rule)',
          borderRadius: 8,
          padding: '0.85rem 1rem',
          margin: '0 0 var(--sp-md)',
        }}
      >
        <Fact label="Seat" value={shire.seat} />
        <Fact label="Population" value={shire.population} />
        <Fact label="Area" value={shire.area} />
        {content.highlights.slice(0, 3).map((h) => (
          <Fact key={h.label} label={h.label} value={h.value} />
        ))}
      </dl>

      {/* 3. Wayfinder strip — real nav to every sub-topic */}
      <ShireNeedBar shire={shire} />

      {/* 3a. Plain-English split between council and state matters */}
      <div style={{ marginTop: 'var(--sp-md)' }}>
        <ResponsibilitiesExplainer variant="inline" />
      </div>


      {/* 4. Narrative paragraph */}
      {narrative && (
        <p style={{ marginTop: 'var(--sp-md)', marginBottom: 'var(--sp-md)', lineHeight: 1.55 }}>
          {narrative}
        </p>
      )}

      {/* 5. Current local priorities */}
      {priorities && (
        <section
          aria-labelledby={`priorities-${shire.id}`}
          style={{
            marginBottom: 'var(--sp-lg)',
            background: 'var(--cream)',
            border: '1px solid var(--rule)',
            borderRadius: 8,
            padding: '0.95rem 1.1rem',
          }}
        >
          <h2
            id={`priorities-${shire.id}`}
            style={{
              fontSize: '1.05rem',
              color: 'var(--green)',
              margin: '0 0 0.5rem',
              fontFamily: 'var(--font-heading)',
            }}
          >
            Current local priorities
          </h2>
          <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>{priorities}</p>
          <p
            style={{
              marginTop: '0.5rem',
              fontSize: '0.78rem',
              color: 'var(--ink-mid)',
              fontStyle: 'italic',
            }}
          >
            From constituent feedback and community conversations, 2024–25.
          </p>
        </section>
      )}

      {/* 6. Condensed "When to contact me instead" */}
      <section
        aria-labelledby={`contact-me-${shire.id}`}
        style={{
          marginBottom: 'var(--sp-lg)',
          background: 'var(--cream)',
          border: '1px solid var(--rule)',
          borderRadius: 8,
          padding: '0.95rem 1.1rem',
        }}
      >
        <h2
          id={`contact-me-${shire.id}`}
          style={{
            fontSize: '1.05rem',
            color: 'var(--green)',
            margin: '0 0 0.5rem',
            fontFamily: 'var(--font-heading)',
          }}
        >
          When to contact me instead
        </h2>
        <p style={{ fontSize: '0.92rem', margin: '0 0 0.5rem', lineHeight: 1.45 }}>
          Reach out when your issue sits with a <strong>WA State Government</strong> agency rather
          than the local council:
        </p>
        <ul
          style={{
            paddingLeft: '1.2rem',
            margin: '0 0 0.75rem',
            fontSize: '0.92rem',
            lineHeight: 1.5,
          }}
        >
          <li>WA Country Health Service — hospitals and health</li>
          <li>State schools, TAFE and Main Roads WA</li>
          <li>WA Police and the Housing Authority</li>
        </ul>
        <p style={{ fontSize: '0.9rem', margin: '0 0 0.25rem' }}>
          <strong>Phone:</strong>{' '}
          <a href="tel:+61898483171" style={{ color: 'var(--rivergum)' }}>
            (08) 9848 3171
          </a>{' '}
          · <strong>Email:</strong>{' '}
          <a href="mailto:bevan.eatts@mp.wa.gov.au" style={{ color: 'var(--rivergum)' }}>
            bevan.eatts@mp.wa.gov.au
          </a>
        </p>
        <p style={{ fontSize: '0.85rem', color: 'var(--ink-mid)', margin: 0 }}>
          5/78 Strickland Street, Denmark WA 6333 · Member for Warren-Blackwood (Nationals WA)
        </p>
      </section>

      {/* 7. Quick Access footer */}
      <div id={`quick-access-${shire.id}`} style={{ marginTop: 'var(--sp-md)' }}>
        <ShireQuickAccess shire={shire} />
      </div>
    </main>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt
        style={{
          fontSize: 11,
          color: 'var(--ink-mid)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, fontSize: '0.95rem', color: 'var(--ink)' }}>{value}</dd>
    </div>
  );
}
